import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  COMING_SOON_MESSAGE_MAX,
  COMING_SOON_TITLE_MAX,
  featureCatalogEntry,
  type FeatureFlagKey,
  type FeatureVisibility,
} from "@klubora/shared";

import { useTenant } from "../hooks/useTenant";
import { useTenantFeatureFlags, type FeatureFlagState } from "../hooks/useTenantFeatureFlags";
import { useTenantPlanCuts } from "../hooks/useTenantPlanCuts";
import { cutCaption, planNamesThatCut } from "../lib/planCuts";
import { writeFeatureFlag } from "../lib/writeFeatureFlag";
import { ComingSoonPosterField } from "../components/editor/ComingSoonPosterField";
import { EditorFooter } from "../components/editor/EditorFooter";
import { TextField } from "../components/editor/TextField";
import { TextAreaField } from "../components/editor/TextAreaField";
import { colors, fontSize, fontWeight, radius, shadow, space } from "../lib/tokens";

/**
 * Platform → Tenant → Features (KLUB-119 / #324, U10).
 *
 * The surface that did not exist anywhere: until now, turning a feature on or
 * off for a club meant an UPDATE by hand. The three states — `on`,
 * `coming_soon`, `hidden` — are what the club actually asks for at launch, and
 * `coming_soon` is the one with copy attached: the icon stays put and the
 * screen carries a title and a description the club can write.
 *
 * `hidden` gets no copy fields on purpose. It is reached only by deep link into
 * a feature the club chose not to open, and there is no launch to announce.
 */

const VISIBILITY_OPTIONS: { value: FeatureVisibility; label: string }[] = [
  { value: "on", label: "Ativo" },
  { value: "coming_soon", label: "Em breve" },
  { value: "hidden", label: "Oculto" },
];

interface Draft {
  visibility: FeatureVisibility;
  comingSoonTitle: string;
  comingSoonMessage: string;
  comingSoonImageUrl: string;
}

type DraftMap = Record<string, Draft>;

function toDraft(flag: FeatureFlagState): Draft {
  return {
    visibility: flag.visibility,
    comingSoonTitle: flag.comingSoonTitle,
    comingSoonMessage: flag.comingSoonMessage,
    comingSoonImageUrl: flag.comingSoonImageUrl,
  };
}

/** Human-readable tail for a rejected save, or empty when there is nothing to add. */
function detailOf(result: PromiseSettledResult<unknown> | undefined): string {
  if (!result || result.status !== "rejected") return "";
  const reason = result.reason;
  const message = reason instanceof Error ? reason.message : String(reason);
  return message ? ` ${message}` : "";
}

function isDirty(draft: Draft, flag: FeatureFlagState): boolean {
  return draft.visibility !== flag.visibility
    || draft.comingSoonTitle !== flag.comingSoonTitle
    || draft.comingSoonMessage !== flag.comingSoonMessage
    || draft.comingSoonImageUrl !== flag.comingSoonImageUrl;
}

export function TenantFeaturesPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { tenant, loading: tenantLoading } = useTenant(slug);
  const { flags, loading, error: loadError, refresh } = useTenantFeatureFlags(tenant?.id ?? null);
  const { plans, loading: cutsLoading, error: cutsError } = useTenantPlanCuts(tenant?.id ?? null);

  // Only what the OPERATOR typed lives in state; everything else is derived
  // from the server on render. The obvious alternative — a full `drafts` map
  // re-synced by an effect on every `flags` change — loses whatever is being
  // typed the moment a realtime event lands, which for this screen means every
  // save (one row per changed feature) and every edit by a second operator.
  const [overrides, setOverrides] = useState<Partial<DraftMap>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const drafts = useMemo<DraftMap>(
    () => Object.fromEntries(flags.map((f) => [f.key, overrides[f.key] ?? toDraft(f)])),
    [flags, overrides],
  );

  const dirtyKeys = useMemo(
    () => flags.filter((f) => isDirty(drafts[f.key], f)).map((f) => f.key),
    [flags, drafts],
  );

  // Only copy that is VISIBLE can block the save. Counting a hidden field
  // would disable Salvar over text the operator cannot see to fix — the fields
  // render only while a feature sits in `coming_soon`.
  const tooLongKey = useMemo(
    () => Object.entries(drafts).find(([, d]) =>
      d.visibility === "coming_soon"
      && (d.comingSoonTitle.length > COMING_SOON_TITLE_MAX
        || d.comingSoonMessage.length > COMING_SOON_MESSAGE_MAX))?.[0],
    [drafts],
  );

  const patch = (key: FeatureFlagKey, next: Partial<Draft>) =>
    setOverrides((o) => ({ ...o, [key]: { ...drafts[key], ...next } }));

  /** Drops every local edit; the render falls back to the server's answer. */
  const onCancel = () => setOverrides({});

  const onSave = async () => {
    if (!tenant) return;
    setSaving(true);
    setError(null);

    // One call per changed feature — the write function takes a single flag, so
    // its validation (key in catalog, visibility in the enum, copy within
    // limits) stays per-row instead of half-applying a batch. They go out
    // concurrently: the rows are independent, and serial round trips would make
    // a seven-feature onboarding pass seven times slower for no guarantee.
    //
    // `allSettled`, not `all`: with `all` the first rejection hides which of the
    // others also failed, and the ones already in flight land anyway. Naming the
    // failed features is the difference between "erro ao salvar" and knowing
    // which switch did not move.
    const results = await Promise.allSettled(
      dirtyKeys.map((key) => {
        const draft = drafts[key];
        return writeFeatureFlag(tenant.id, key as FeatureFlagKey, draft.visibility, {
          coming_soon_title: draft.comingSoonTitle,
          coming_soon_message: draft.comingSoonMessage,
          coming_soon_image_url: draft.comingSoonImageUrl,
        });
      }),
    );

    const failed = dirtyKeys.filter((_, i) => results[i].status === "rejected");

    // Re-read before touching the drafts. Clearing an override while `flags`
    // still holds the pre-save row is what made a successful save appear to
    // revert: the derived draft falls back to stale server state.
    await refresh();

    if (failed.length > 0) {
      const labels = failed.map((k) => featureCatalogEntry(k)?.label ?? k).join(", ");
      const first = results.find((r) => r.status === "rejected");
      setError(`Falha ao salvar: ${labels}.${detailOf(first)}`);
      // Drop only the overrides that landed; the failed ones stay dirty so the
      // operator can retry without retyping.
      setOverrides((o) => {
        const kept: Partial<DraftMap> = {};
        for (const key of failed) if (o[key]) kept[key] = o[key];
        return kept;
      });
    } else {
      setOverrides({});
    }
    setSaving(false);
  };

  if (tenantLoading || loading || !tenant) {
    return <div style={{ color: colors.stone, padding: space.lg }}>Carregando...</div>;
  }

  if (loadError) {
    return (
      <div style={{
        padding: space.md, background: colors.errorSoft, color: colors.error,
        borderRadius: radius.sm, fontSize: fontSize.md,
      }}>
        Não foi possível carregar as features deste clube: {loadError}
      </div>
    );
  }

  const tabs = flags.filter((f) => featureCatalogEntry(f.key)?.surface === "tab");
  const others = flags.filter((f) => featureCatalogEntry(f.key)?.surface !== "tab");

  const renderRow = (flag: FeatureFlagState, isLast: boolean) => {
    const draft = drafts[flag.key] ?? toDraft(flag);
    const entry = featureCatalogEntry(flag.key);
    const showCopy = draft.visibility === "coming_soon";
    const showCuts = draft.visibility === "on" || draft.visibility === "coming_soon";
    const caption = showCuts && !cutsLoading && !cutsError
      ? cutCaption(planNamesThatCut(plans, flag.key))
      : null;

    return (
      <div key={flag.key}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: space.lg, flexWrap: "wrap",
          padding: `${space.md}px ${space.lg}px`,
          borderBottom: isLast && !showCopy ? "none" : `1px solid ${colors.fog}`,
        }}>
          <div>
            <div style={{ fontSize: fontSize.md, fontWeight: fontWeight.medium }}>{flag.label}</div>
            <div style={{ fontSize: fontSize.xs, color: colors.stoneSoft, marginTop: 3 }}>
              {SURFACE_HINT[entry?.surface ?? "route"]}
              {!flag.persisted && " · ainda não configurada para este clube"}
            </div>
            {showCuts && cutsLoading && (
              <div style={{ fontSize: fontSize.xs, color: colors.stoneSoft, marginTop: 6 }}>
                Verificando planos…
              </div>
            )}
            {showCuts && cutsError && (
              <div style={{ fontSize: fontSize.xs, color: colors.warning, marginTop: 6 }}>
                Não foi possível ler os cortes por plano.
              </div>
            )}
            {caption && (
              <div style={{ fontSize: fontSize.xs, color: colors.warning, marginTop: 6 }}>
                {caption}
              </div>
            )}
          </div>
          <VisibilitySelector
            value={draft.visibility}
            onChange={(visibility) => patch(flag.key, { visibility })}
          />
        </div>

        {showCopy && (
          <div style={{
            padding: `${space.lg}px ${space.lg}px ${space.lg}px`,
            background: colors.paper,
            borderBottom: isLast ? "none" : `1px solid ${colors.fog}`,
          }}>
            <TextField
              label="Título do aviso"
              value={draft.comingSoonTitle}
              placeholder={entry?.comingSoonTitle}
              onChange={(comingSoonTitle) => patch(flag.key, { comingSoonTitle })}
              error={draft.comingSoonTitle.length > COMING_SOON_TITLE_MAX
                ? `Máximo de ${COMING_SOON_TITLE_MAX} caracteres.`
                : null}
            />
            <TextAreaField
              label="Descrição"
              value={draft.comingSoonMessage}
              placeholder={entry?.comingSoonMessage}
              maxLength={COMING_SOON_MESSAGE_MAX}
              hint="Em branco usa o texto padrão da Klubora."
              onChange={(comingSoonMessage) => patch(flag.key, { comingSoonMessage })}
            />
            <ComingSoonPosterField
              tenantId={tenant.id}
              featureKey={flag.key}
              value={draft.comingSoonImageUrl}
              title={draft.comingSoonTitle || entry?.comingSoonTitle || "Em breve"}
              message={draft.comingSoonMessage || entry?.comingSoonMessage || ""}
              onChange={(comingSoonImageUrl) => patch(flag.key, { comingSoonImageUrl })}
            />
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (title: string, group: FeatureFlagState[]) => (
    <section style={{ marginBottom: space.xxl }}>
      <h3 style={{
        fontSize: fontSize.md, fontWeight: fontWeight.semibold,
        color: colors.ink, marginBottom: space.md,
      }}>
        {title}
      </h3>
      <div style={{
        background: colors.white, border: `1px solid ${colors.fog}`,
        borderRadius: radius.md, boxShadow: shadow.sm, overflow: "hidden",
      }}>
        {group.map((f, i) => renderRow(f, i === group.length - 1))}
      </div>
    </section>
  );

  return (
    <div>
      <p style={{
        color: colors.stone, fontSize: fontSize.md, lineHeight: 1.5,
        margin: `0 0 ${space.xl}px`, maxWidth: 620,
      }}>
        O que o clube enxerga no app. <strong style={{ fontWeight: fontWeight.medium }}>Em breve</strong>{" "}
        mantém o ícone no lugar e mostra um aviso;{" "}
        <strong style={{ fontWeight: fontWeight.medium }}>Oculto</strong> tira da navegação.
      </p>

      {renderGroup("Abas do app", tabs)}
      {renderGroup("Rotas e componentes", others)}

      <EditorFooter
        dirty={dirtyKeys.length > 0}
        saving={saving}
        error={error ?? loadError}
        saveBlocked={tooLongKey !== undefined}
        status={
          tooLongKey !== undefined
            ? "Texto acima do limite."
            : dirtyKeys.length > 0
              ? `${dirtyKeys.length} ${dirtyKeys.length === 1 ? "alteração não salva" : "alterações não salvas"}`
              : ""
        }
        onCancel={onCancel}
        onSave={onSave}
      />
    </div>
  );
}

const SURFACE_HINT: Record<string, string> = {
  tab: "Aba — tem ícone na barra inferior",
  route: "Rota — sem ícone próprio",
  component: "Componente — dentro de outra tela",
};

interface SelectorProps {
  value: FeatureVisibility;
  onChange: (next: FeatureVisibility) => void;
}

/**
 * Three mutually exclusive states in one control. A dropdown would hide two of
 * the three behind a click, and the whole point of this screen is that the
 * operator sees, per feature, which of the three it is sitting in.
 */
function VisibilitySelector({ value, onChange }: SelectorProps) {
  return (
    <div
      role="radiogroup"
      style={{
        display: "inline-flex", border: `1px solid ${colors.fog}`,
        borderRadius: radius.sm, overflow: "hidden", background: colors.white,
      }}
    >
      {VISIBILITY_OPTIONS.map((option, i) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            style={{
              appearance: "none", border: "none", font: "inherit",
              fontSize: fontSize.sm, padding: `6px ${space.md}px`,
              whiteSpace: "nowrap", cursor: "pointer",
              borderRight: i < VISIBILITY_OPTIONS.length - 1 ? `1px solid ${colors.fog}` : "none",
              background: selected ? SELECTED_BG[option.value] : "transparent",
              color: selected ? colors.white : colors.stone,
              fontWeight: selected ? fontWeight.medium : fontWeight.regular,
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

// The selected state carries the semantic colour so the operator reads the
// consequence, not just the position: live is success, coming_soon is the
// warning amber already used for delinquent tenants, hidden is neutral ink.
const SELECTED_BG: Record<FeatureVisibility, string> = {
  on: colors.success,
  coming_soon: colors.warning,
  hidden: colors.ink,
};
